import {memo} from 'react';
import MUIGrid from '@/components/MUI-Components/MUI-Grid';
import MUITypography from '@/components/MUI-Components/MUI-Typography';
import DisplayImage from '../display-image';
import Box from '@mui/material/Box';
import type {SxProps, Theme} from '@mui/material/styles';
import {colors, fontFamily, fontSize, fontWeight, lineHeight, spacing} from '@/styles/tokens';

export type CaseStudyPlantSize = 'digi-sign' | 'edc';

// Migrated from globals.scss `.digi-sign-img img` (width/height:66px, !important — needed to
// beat DisplayImage's own `autoStyle` inline width:auto/height:auto, which applies whenever a
// StaticImageData source has no explicit width/height, as here) and `.edc-img img` (60px, no
// !important). The `edc` variant's declaration has therefore never actually won against that
// same inline style — its 4 consumer pages have always rendered this image at its intrinsic
// size, not 60x60. Preserved exactly as-is (including the non-!important edc case, which stays
// a no-op) — flagged here for a separate, deliberate decision, not fixed as part of this pass.
const plantImgSizeSx: Record<CaseStudyPlantSize, SxProps<Theme>> = {
    'digi-sign': {width: '66px !important', height: '66px !important'},
    edc: {width: '60px', height: '60px'}
};

// Migrated from globals.scss `.casestudy-banner-img` (+ nested `span`, + the grouped
// `.unique-school-banner-heading, .p2b-heading, .business-strategy, .casestudy-banner-img,
// .integraledger-heading` mobileScreen (max-width:430px) font-size/line-height override — only the
// `.casestudy-banner-img` member of that group is this component's; the other selectors in the
// group belong to other components/pages and are left untouched/global). Sole owner confirmed —
// `.plant-img`, applied on the sibling Box above, is explicitly NOT this component's and is left
// fully untouched and global.
const casestudyBannerImgSx = {
    fontFamily: `${fontFamily.heading} !important`,
    fontSize: '55px !important', // no exact token match (closest is fontSize[56]) — preserved for parity
    fontWeight: `${fontWeight.bold} !important`,
    color: colors.text.inverse,
    lineHeight: '70px !important', // no exact token match — preserved for parity
    '& span': {color: 'RGB(255, 211, 53)'}, // case-study brand accent, no token match — preserved for parity
    '@media (max-width: 430px)': {
        fontSize: '26px !important', // no exact token match — preserved for parity
        lineHeight: `${lineHeight[40]} !important`
    }
} as const;

// Migrated from globals.scss `.banner-div`. Sole owner confirmed.
const bannerDivSx = {
    backgroundColor: 'RGB(51, 115, 97)', // case-study brand color, no token match — preserved for parity
    padding: spacing[20],
    position: 'relative'
} as const;

// Migrated from globals.scss `.banner-client`. Sole owner confirmed.
const bannerClientSx = {
    fontFamily: `${fontFamily.body} !important`,
    fontSize: `${fontSize[16]} !important`,
    fontWeight: `${fontWeight.regular} !important`,
    color: colors.text.inverse
} as const;

// Migrated from globals.scss `.banner-client-product`. Sole owner confirmed.
const bannerClientProductSx = {
    fontFamily: `${fontFamily.heading} !important`,
    fontSize: `${fontSize[24]} !important`,
    lineHeight: `${lineHeight[32]} !important`,
    fontWeight: `${fontWeight.bold} !important`,
    color: colors.text.inverse
} as const;

const CaseStudyBanner = (props: any) => {
    const {
        techstack,
        product,
        service,
        plantimg,
        headingclass,
        backgroundclass,
        heading,
        plantclass,
        plantSize,
        headingLevel = 'h1',
        backgroundSx,
        headingSx
    } = props;

    return (
        <Box>
            <MUIGrid container>
                <MUIGrid item xs={12} sm={12} md={6} lg={6} className="banner-grid">
                    <Box className={`banner-div ${backgroundclass}`} sx={{...bannerDivSx, ...backgroundSx}}>
                        <Box className={`${plantclass ?? ''} plant-img`}>
                            <DisplayImage
                                src={plantimg}
                                alt="plant-banner-img"
                                sx={plantSize ? plantImgSizeSx[plantSize as CaseStudyPlantSize] : undefined}
                            />
                        </Box>

                        <MUITypography
                            component={headingLevel}
                            className={`casestudy-banner-img ${headingclass}`}
                            sx={{...casestudyBannerImgSx, ...headingSx}}
                        >
                            {heading}
                        </MUITypography>

                        <MUIGrid container className="top-spacing">
                            <MUIGrid item xs={12} sm={12} md={6} lg={6}>
                                <MUITypography className="banner-client" sx={bannerClientSx}>
                                    Client{' '}
                                </MUITypography>

                                <MUITypography className="banner-client-product" sx={bannerClientProductSx}>
                                    {product}
                                </MUITypography>
                            </MUIGrid>

                            <MUIGrid item xs={12} sm={12} md={6} lg={6}>
                                <MUITypography className="banner-client" sx={bannerClientSx}>
                                    Industry{' '}
                                </MUITypography>

                                <MUITypography className="banner-client-product" sx={bannerClientProductSx}>
                                    {service}
                                </MUITypography>
                            </MUIGrid>
                        </MUIGrid>

                        <MUITypography className="banner-client-product top-spacing" sx={bannerClientProductSx}>
                            Tech Stack
                        </MUITypography>

                        <MUIGrid container paddingTop={3}>
                            {techstack?.map((data: any, index: number) => (
                                <MUIGrid item xs={4} sm={2.4} md={2.4} lg={2.4} key={index}>
                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            margin: '0 auto'
                                        }}
                                    >
                                        <DisplayImage
                                            src={data.img}
                                            alt={data?.text}
                                            loading="lazy"
                                            width={60}
                                            height={60}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain'
                                            }}
                                        />
                                    </Box>

                                    <MUITypography className="banner-client" sx={bannerClientSx}>
                                        {data.text}
                                    </MUITypography>
                                </MUIGrid>
                            ))}
                        </MUIGrid>
                    </Box>
                </MUIGrid>

                <MUIGrid item xs={12} sm={12} md={5} lg={5}></MUIGrid>
            </MUIGrid>
        </Box>
    );
};

export default memo(CaseStudyBanner);
